import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Coins, Users } from 'lucide-react';
import type { CoutBreakdownItem } from '@/hooks/useEquipeStats';

interface ModalCoutJourBreakdownProps {
  open: boolean;
  onClose: () => void;
  breakdown: CoutBreakdownItem[];
  coutTotal: number;
}

/**
 * Modal affichant le détail des coûts par membre actif aujourd'hui
 * Les données sont pré-calculées par useEquipeStats (même logique que le total KPI)
 */
export function ModalCoutJourBreakdown({ open, onClose, breakdown, coutTotal }: ModalCoutJourBreakdownProps) {
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Coins className="h-5 w-5 text-amber-500" />
            Détail des coûts aujourd'hui
          </DialogTitle>
          <DialogDescription>
            Répartition des coûts de main d'œuvre par membre affecté aujourd'hui
          </DialogDescription>
        </DialogHeader>

        {/* Résumé */}
        <div className="flex items-center justify-between bg-muted/50 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-full">
              <Users className="h-5 w-5 text-amber-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Membres affectés</p>
              <p className="text-lg font-bold">{breakdown.length}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Coût total</p>
            <p className="text-2xl font-black text-amber-500">{formatCurrency(coutTotal)}</p>
          </div>
        </div>

        {/* Tableau des coûts */}
        <div className="flex-1 overflow-auto">
          {breakdown.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Aucun membre affecté aujourd'hui</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Membre</TableHead>
                  <TableHead>Poste</TableHead>
                  <TableHead className="text-right">Taux/h</TableHead>
                  <TableHead className="text-right">Coef.</TableHead>
                  <TableHead className="text-right">Coût/jour</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {breakdown.map((m) => (
                  <TableRow key={m.membreId}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{m.nom}</span>
                        {m.valueJour < 1 && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                            ½ jour
                          </Badge>
                        )}
                      </div>
                      {m.specialite && (
                        <div className="text-xs text-muted-foreground">{m.specialite}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {m.poste || 'Non défini'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(m.tauxHoraire)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      x{m.coefficientCharges}
                    </TableCell>
                    <TableCell className="text-right font-bold text-amber-500">
                      {formatCurrency(m.coutJour)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Footer avec formule */}
        <div className="mt-4 pt-4 border-t text-xs text-muted-foreground">
          <p>
            <strong>Formule :</strong> Taux horaire × coefficient charges × heures/jour × durée
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
